import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable } from "rxjs";

import { AuthenticationService } from "../services/authentication.service";
import { ActivatedRoute } from "@angular/router";
import { EnvironmentConfigsLoaderService } from "../services/environment-configs-loader.service";
import { LoadingService } from "../services/loading.service";
import { LoadingStepsEnum } from "src/app/shared/components/loading/loading-steps.enum";
import { finalize } from "rxjs/operators";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private screenerWebUrl: string;

  constructor(
    environmentConfigs: EnvironmentConfigsLoaderService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private loadingService: LoadingService
  ) {
    this.screenerWebUrl = environmentConfigs.ScreenerWebUrl;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let currentUser = this.authenticationService.currentUserValue;
    if (currentUser && currentUser.userId) {
      let requestUrl = "";

      if (request.url.indexOf('assets') >= 0) {
        requestUrl = location.origin + location.pathname + request.url;

        request = request.clone({
          url: requestUrl
        });
      }
      else if (currentUser.tenantCode) {
        requestUrl = `/${this.screenerWebUrl}/${currentUser.tenantCode}/${request.url}`;
      } else {
        requestUrl = `/${this.screenerWebUrl}/${request.url}`;
      }

      // The session token is delivered via a secure HttpOnly cookie rather than being read
      // from storage and sent as an Authorization header (PBI 14763). withCredentials makes
      // the browser attach that cookie; the XSRF-TOKEN cookie is echoed back as a header for
      // state-changing requests as a double-submit CSRF defense.
      request = request.clone({
        url: requestUrl,
        withCredentials: true,
        setHeaders: this.buildCsrfHeaders(request.method),
      });
    } else if (request.url.indexOf("token") < 0) {
      let tenantCodePath = this.readTenantCodeFromRoute();

      request = request.clone({
        url: `/${this.screenerWebUrl}/${tenantCodePath + request.url}`,
        withCredentials: true,
        setHeaders: this.buildCsrfHeaders(request.method),
      });
    } else {
      request = request.clone({
        url: `/${this.screenerWebUrl}/${request.url}`,
        withCredentials: true,
      });
    }

    this.loadingService.setLoadingIndicatorStep(LoadingStepsEnum.Loading);
    return next.handle(request).pipe(
      finalize(() => {
        this.loadingService.setLoadingIndicatorStep(LoadingStepsEnum.Content);
      })
    );
  }

  private buildCsrfHeaders(method: string): { [name: string]: string } {
    const unsafeMethods = ["POST", "PUT", "PATCH", "DELETE"];
    if (unsafeMethods.indexOf((method || "").toUpperCase()) < 0) {
      return {};
    }

    const csrfToken = this.readCookie("XSRF-TOKEN");
    return csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {};
  }

  private readCookie(name: string): string {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? decodeURIComponent(match[2]) : null;
  }

  private readTenantCodeFromRoute(): string {
    if (this.route.firstChild && this.route.firstChild.firstChild && this.route.firstChild.firstChild.snapshot) {
      let tenantCode = this.route.firstChild.firstChild.snapshot.paramMap.get(
        "tenantCode"
      );
      return tenantCode ? tenantCode + "/" : "";
    }
    return "";
  }
}
